import { Component, Input, Output, EventEmitter, HostListener, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'print';

/**
 * Modal genérico e reutilizável.
 *
 * Uso básico:
 * ```html
 * <app-modal [visible]="show" title="Meu Título" (close)="show = false">
 *   <p>Conteúdo do modal</p>
 * </app-modal>
 * ```
 */
@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
})
export class ModalComponent implements OnDestroy {
  /** Controla a visibilidade do modal. */
  @Input() visible = false;

  /** Título exibido no cabeçalho. */
  @Input() title = '';

  /** Subtítulo opcional abaixo do título. */
  @Input() subtitle = '';

  /** Largura predefinida: 'sm' 400px | 'md' 560px | 'lg' 720px | 'xl' 960px */
  @Input() size: ModalSize = 'md';

  /** Emitido quando o usuário clica no backdrop ou no botão de fechar. */
  @Output() close = new EventEmitter<void>();

  /** z-index do backdrop. Use valor maior (ex: 1010) para modais aninhados. */
  @Input() zIndex = 1000;

  /** Permite fechar arrastando para baixo no mobile (bottom sheet). */
  @Input() swipeToClose = false;

  private modalEl: HTMLElement | null = null;
  private swipeTouchStartY = 0;
  private swipeIsDragging = false;
  private boundTouchMove: ((e: TouchEvent) => void) | null = null;

  @ViewChild('modalEl') set modalElRef(ref: ElementRef<HTMLElement> | undefined) {
    if (this.modalEl && this.boundTouchMove) {
      this.modalEl.removeEventListener('touchmove', this.boundTouchMove);
    }

    this.modalEl = ref ? ref.nativeElement : null;

    if (this.modalEl && this.swipeToClose) {
      this.boundTouchMove = this.onTouchMove.bind(this);
      this.modalEl.addEventListener('touchmove', this.boundTouchMove, { passive: false });
    }
  }

  ngOnDestroy(): void {
    if (this.modalEl && this.boundTouchMove) {
      this.modalEl.removeEventListener('touchmove', this.boundTouchMove);
    }
  }

  onTouchStart(e: TouchEvent): void {
    if (!this.swipeToClose) return;
    this.swipeTouchStartY = e.touches[0].clientY;
    this.swipeIsDragging = false;
  }

  private onTouchMove(e: TouchEvent): void {
    if (!this.swipeToClose || !this.modalEl) return;
    const delta = e.touches[0].clientY - this.swipeTouchStartY;
    const scrollEl = this.modalEl.querySelector('.modal__body') || this.modalEl;
    if (scrollEl.scrollTop === 0 && delta > 0) {
      this.swipeIsDragging = true;
      e.preventDefault();
      this.modalEl.style.transition = 'none';
      this.modalEl.style.transform = `translateY(${delta}px)`;
    }
  }

  onTouchEnd(e: TouchEvent): void {
    if (!this.swipeToClose || !this.swipeIsDragging || !this.modalEl) return;
    this.swipeIsDragging = false;
    const delta = e.changedTouches[0].clientY - this.swipeTouchStartY;
    if (delta > 80) {
      this.modalEl.style.transition = 'transform .25s ease';
      this.modalEl.style.transform = `translateY(110%)`;
      setTimeout(() => {
        this.close.emit();
        if (this.modalEl) {
          this.modalEl.style.transition = '';
          this.modalEl.style.transform = '';
        }
      }, 230);
    } else {
      this.modalEl.style.transition = 'transform .25s ease';
      this.modalEl.style.transform = '';
      setTimeout(() => {
        if (this.modalEl) this.modalEl.style.transition = '';
      }, 260);
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (!this.visible) return;
    // Don't close if a nested modal with a higher z-index is currently open
    const backdrops = document.querySelectorAll<HTMLElement>('.modal-backdrop');
    let highestZ = 0;
    backdrops.forEach(el => {
      const z = parseInt(el.style.zIndex || '0', 10);
      if (z > highestZ) highestZ = z;
    });
    if (highestZ > this.zIndex) return;
    // Stop other modal instances from also handling this event
    event.stopImmediatePropagation();
    this.close.emit();
  }
}
